import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { publicApi } from '../utils/publicApi';

const SPEAKER_TYPES = ['Government', 'Private Sector', 'International'];

/** Map API/DB values to canonical types so filters match (trim, case, common variants). */
function normalizeSpeakerType(raw) {
  const t = String(raw ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/[\u200e\u200f\u202a-\u202e]/g, '')
    .trim();
  if (!t) return 'Private Sector';

  const k = t.toLowerCase().replace(/\s+/g, ' ');

  const direct = {
    government: 'Government',
    gov: 'Government',
    'private sector': 'Private Sector',
    private: 'Private Sector',
    international: 'International',
    intl: 'International',
  };
  if (direct[k]) return direct[k];

  for (const canon of SPEAKER_TYPES) {
    if (canon.toLowerCase() === k) return canon;
  }

  if (k.includes('international')) return 'International';
  if (k.includes('government') || k.includes('govt')) return 'Government';
  if (k.includes('private')) return 'Private Sector';

  return t;
}

const LinkedInIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
      fill="#422A7E"
    />
  </svg>
);

const DefaultSpeakerPhoto = ({ className = '' }) => (
  <div
    className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#E8EEF5] to-[#D4DCE8] ${className}`}
    aria-hidden
  >
    <svg className="w-[42%] h-[42%] text-[#B4B9C9]" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  </div>
);

const SpeakerPhoto = ({ src, imgClassName }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return <DefaultSpeakerPhoto />;
  }
  return (
    <img
      src={src}
      alt=""
      className={imgClassName}
      onError={() => setFailed(true)}
    />
  );
};

const SPEAKER_CAROUSEL_FADE = {
  initial: { opacity: 0.75 },
  animate: { opacity: 1 },
  exit: { opacity: 0.75 },
};

const SpeakerPeekCard = ({ speaker, side, onActivate }) => {
  const { t } = useLanguage();
  if (!speaker) {
    return <div className="w-[72px] shrink-0 sm:w-20 opacity-0" aria-hidden />;
  }

  const isLeft = side === 'left';

  return (
    <motion.button
      type="button"
      onClick={onActivate}
      className="flex w-[72px] shrink-0 flex-col items-center sm:w-20"
      aria-label={isLeft ? t('website.speakers.carouselPrev') : t('website.speakers.carouselNext')}
      whileTap={{ scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
    >
      <div
        className="relative aspect-square w-full overflow-hidden rounded-full bg-[#EEF1F6]"
        style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)' }}
      >
        <SpeakerPhoto src={speaker.img} imgClassName="absolute inset-0 h-full w-full object-cover" />
      </div>
    </motion.button>
  );
};

const SpeakerHeroCard = ({ speaker, onOpenDetails }) => {
  const { t } = useLanguage();
  if (!speaker) return null;

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenDetails(speaker);
        }
      }}
      onClick={() => onOpenDetails(speaker)}
      className="relative z-10 flex w-[min(76vw,280px)] shrink-0 cursor-pointer flex-col items-center gap-2 px-2 pb-0 pt-0 text-center sm:w-[min(72vw,300px)] sm:gap-2.5"
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <div
        className="relative aspect-[192/184] w-[min(152px,42vw)] shrink-0 overflow-hidden rounded-full sm:w-[min(168px,40vw)]"
        style={{ boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)' }}
      >
        <SpeakerPhoto src={speaker.img} imgClassName="absolute inset-0 h-full w-full object-cover" />
      </div>

      <div className="flex w-full flex-col items-center gap-0.5">
        <h3 className="w-full text-[16px] font-semibold leading-snug text-[#0AB0CE] sm:text-[17px]">{speaker.name}</h3>
        {speaker.position ? (
          <p className="w-full text-[13px] font-normal leading-5 text-[#7A8591] sm:text-[14px]">{speaker.position}</p>
        ) : null}
        {speaker.country ? (
          <p className="text-[12px] font-normal leading-5 text-[#B0B9C3]">{speaker.country}</p>
        ) : null}
      </div>

      <span className="mt-1 rounded-full bg-[#0AB0CE] px-5 py-2 text-[12px] font-semibold text-white sm:px-6 sm:text-[13px]">
        {t('website.speakers.details')}
      </span>
    </div>
  );
};

const Speakers = () => {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [mobileSpeakerIndex, setMobileSpeakerIndex] = useState(0);
  const { t, isRTL } = useLanguage();
  const touchStartX = useRef(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  const openSpeakerDetails = (speaker) => {
    setSelectedSpeaker(speaker);
    document.body.style.overflow = 'hidden';
  };

  const closeSpeakerDetails = () => {
    setSelectedSpeaker(null);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const fetchSpeakers = async () => {
      try {
        setLoading(true);
        const data = await publicApi.getSpeakers();
        const list = Array.isArray(data) ? data : [];

        if (list.length > 0) {
          setError(null);
          const processedData = list.map((speaker) => {
            const position = speaker.position || '';
            const company = speaker.company || '';
            const role = [position, company].filter(Boolean).join(company ? ' at ' : '') || position;
            const rawImg = speaker.img ? publicApi.toAbsoluteUrl(speaker.img) : null;
            const img = rawImg && String(rawImg).trim() ? rawImg : null;
            return {
              id: speaker.id,
              name: speaker.name,
              position,
              company,
              country: speaker.country || '',
              role,
              speaker_type: normalizeSpeakerType(speaker.speaker_type),
              gender: speaker.gender,
              img,
              linkedin_url: (speaker.linkedin_url || '').trim(),
              description: speaker.description || '',
            };
          });
          setSpeakers(processedData);
        } else {
          setError(null);
          setSpeakers([]);
        }
      } catch (err) {
        console.error('Error fetching speakers:', err);
        setError(err.message);
        setSpeakers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSpeakers();
  }, []);

  const filteredSpeakers = useMemo(() => {
    if (typeFilter === 'all') return speakers;
    return speakers.filter((s) => {
      const st = String(s.speaker_type || '').trim();
      return st === typeFilter;
    });
  }, [speakers, typeFilter]);

  useEffect(() => {
    setMobileSpeakerIndex(0);
  }, [typeFilter, filteredSpeakers.length]);

  const nMobile = filteredSpeakers.length;

  const mobileGoPrev = useCallback(() => {
    if (nMobile <= 0) return;
    setMobileSpeakerIndex((i) => (i - 1 + nMobile) % nMobile);
  }, [nMobile]);

  const mobileGoNext = useCallback(() => {
    if (nMobile <= 0) return;
    setMobileSpeakerIndex((i) => (i + 1) % nMobile);
  }, [nMobile]);

  const mobileGoToIndex = useCallback(
    (idx) => {
      if (idx === mobileSpeakerIndex || nMobile <= 0) return;
      setMobileSpeakerIndex(idx);
    },
    [mobileSpeakerIndex, nMobile]
  );

  const onCarouselTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onCarouselTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (nMobile <= 1) return;
    if (dx > 56) mobileGoPrev();
    else if (dx < -56) mobileGoNext();
  };

  const mobileIdx = nMobile > 0 ? Math.min(mobileSpeakerIndex, nMobile - 1) : 0;
  const mobileCurrent = nMobile > 0 ? filteredSpeakers[mobileIdx] : null;
  const prevIdx = nMobile > 0 ? (mobileIdx - 1 + nMobile) % nMobile : 0;
  const nextIdx = nMobile > 0 ? (mobileIdx + 1) % nMobile : 0;
  const leftSpeaker = nMobile > 0 ? (isRTL ? filteredSpeakers[nextIdx] : filteredSpeakers[prevIdx]) : null;
  const rightSpeaker = nMobile > 0 ? (isRTL ? filteredSpeakers[prevIdx] : filteredSpeakers[nextIdx]) : null;
  const leftCarouselAction = isRTL ? mobileGoNext : mobileGoPrev;
  const rightCarouselAction = isRTL ? mobileGoPrev : mobileGoNext;
  const mobileCarouselTriple = nMobile >= 3;

  const normalizeLinkedInUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2, ease: 'easeIn' },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const AvatarFallback = ({ gender, size = 'sm' }) => {
    const iconClass = size === 'lg' ? 'w-16 h-16' : 'w-12 h-12';
    if (gender === 'female') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={`${iconClass} text-[#9DD4CA]`}>
          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm0 8.625a2.625 2.625 0 100-5.25 2.625 2.625 0 000 5.25z" clipRule="evenodd" />
          <path fillRule="evenodd" d="M12 18.75a.75.75 0 01-.75-.75v-7.5a.75.75 0 011.5 0v7.5a.75.75 0 01-.75.75z" clipRule="evenodd" />
          <path fillRule="evenodd" d="M9.75 12a.75.75 0 01.75-.75h3a.75.75 0 010 1.5h-3a.75.75 0 01-.75-.75z" clipRule="evenodd" />
        </svg>
      );
    }
    if (gender === 'male') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={`${iconClass} text-[#4C078C]`}>
          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm0 8.625a2.625 2.625 0 100-5.25 2.625 2.625 0 000 5.25z" clipRule="evenodd" />
          <path d="M16.5 12a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0V15h-1.5a.75.75 0 010-1.5h1.5v-1.5a.75.75 0 01.75-.75z" />
        </svg>
      );
    }
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={`${iconClass} text-gray-400`}>
        <path fillRule="evenodd" d="M18.685 19.097A9.723 9.723 0 0021.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 003.065 7.097A9.716 9.716 0 0012 21.75a9.716 9.716 0 006.685-2.653zm-12.54-1.285A7.486 7.486 0 0112 15a7.486 7.486 0 015.855 2.812A8.224 8.224 0 0112 20.25a8.224 8.224 0 01-5.855-2.438zM15.75 9a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" clipRule="evenodd" />
      </svg>
    );
  };

  const filterLabel = (typeKey) => {
    if (typeKey === 'all') return t('website.speakers.filterAll');
    if (typeKey === 'Government') return t('website.speakers.filterGovernment');
    if (typeKey === 'Private Sector') return t('website.speakers.filterPrivateSector');
    if (typeKey === 'International') return t('website.speakers.filterInternational');
    return typeKey;
  };

  return (
    <section
      id="speakers"
      className="relative overflow-x-hidden bg-white"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <div className="w-full px-6 sm:px-10 lg:px-16 py-16 sm:py-20 lg:py-28 flex flex-col items-center gap-16 lg:gap-[93px]">
        <div className="w-full max-w-[1280px] flex flex-col items-center gap-16 lg:gap-[93px]">
          {/* Title + subtitle — Figma */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-[768px] flex flex-col items-center gap-6"
          >
            <h2
              className="w-full text-center text-[32px] sm:text-[40px] lg:text-[48px]"
              style={{ color: '#51516B', fontWeight: 700, lineHeight: 1.2 }}
            >
              {t('website.speakers.title')}
            </h2>
            <p
              className="w-full max-w-[749px] text-center text-[15px] sm:text-[16px]"
              style={{ color: '#51516B', fontWeight: 400, lineHeight: '24px' }}
            >
              {t('website.speakers.subtitle')}
            </p>
          </motion.div>

          {/* Type filters — Figma pills (match schedule day tabs) */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-wrap items-center justify-center gap-2 sm:gap-3"
            style={{ gap: 'clamp(8px, 2vw, 49px)' }}
          >
            <button
              type="button"
              onClick={() => setTypeFilter('all')}
              className="min-w-[100px] px-4 sm:min-w-[130px] py-[15px] rounded-[37px] flex items-center justify-center transition-colors duration-200"
              style={{
                background:
                  typeFilter === 'all'
                    ? 'linear-gradient(157deg, #0AB0CE 0%, #111034 100%)'
                    : 'transparent',
              }}
            >
              <span
                className="text-[15px] sm:text-[16px] font-semibold"
                style={{ color: typeFilter === 'all' ? '#EDEAF3' : '#51516B' }}
              >
                {filterLabel('all')}
              </span>
            </button>
            {SPEAKER_TYPES.map((stype) => {
              const active = typeFilter === stype;
              return (
                <button
                  key={stype}
                  type="button"
                  onClick={() => setTypeFilter(stype)}
                  className="min-w-[100px] px-4 py-[15px] rounded-[37px] flex items-center justify-center transition-colors duration-200"
                  style={{
                    background: active
                      ? 'linear-gradient(157deg, #0AB0CE 0%, #111034 100%)'
                      : 'transparent',
                  }}
                >
                  <span
                    className="text-[15px] sm:text-[16px] font-semibold text-center"
                    style={{ color: active ? '#EDEAF3' : '#51516B' }}
                  >
                    {filterLabel(stype)}
                  </span>
                </button>
              );
            })}
          </motion.div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-12 h-12 border-4 border-[#0AB0CE]/20 border-t-[#0AB0CE] rounded-full animate-spin mb-4" />
              <p style={{ color: '#51516B' }}>{t('website.speakers.loading')}</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12 px-4 rounded-2xl bg-red-50 border border-red-100 w-full max-w-lg">
              <p className="text-red-600">
                {t('website.speakers.errorPrefix')} {error}
              </p>
            </div>
          )}

          {!loading && !error && (
            <motion.div
              key={typeFilter}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="w-full flex flex-col items-center gap-6 lg:gap-[22px]"
            >
              {filteredSpeakers.length === 0 ? (
                <motion.p variants={itemVariants} className="text-center py-12" style={{ color: '#51516B' }}>
                  {t('website.speakers.empty')}
                </motion.p>
              ) : (
                <div className="w-full flex flex-col items-center gap-4">
                  {/* Mobile: floating carousel (no tray/card background) */}
                  <div className="lg:hidden flex w-full flex-col items-center gap-3">
                    <div
                      className="relative w-full max-w-[960px] py-1"
                      onTouchStart={onCarouselTouchStart}
                      onTouchEnd={onCarouselTouchEnd}
                    >
                        {nMobile > 1 ? (
                          <>
                            <button
                              type="button"
                              onClick={mobileGoPrev}
                              className="absolute left-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-[#0AB0CE]/40 hover:text-[#0AB0CE] sm:h-10 sm:w-10 md:left-0.5"
                              aria-label={t('website.speakers.carouselPrev')}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path
                                  d="M15 6l-6 6 6 6"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={mobileGoNext}
                              className="absolute right-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-[#0AB0CE]/40 hover:text-[#0AB0CE] sm:h-10 sm:w-10 md:right-0.5"
                              aria-label={t('website.speakers.carouselNext')}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path
                                  d="M9 6l6 6-6 6"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </>
                        ) : null}
                        <div className="flex items-center justify-center gap-2 overflow-visible px-8 sm:gap-4 sm:px-10 md:gap-6 md:px-12">
                          {mobileCarouselTriple ? (
                            <>
                              <SpeakerPeekCard
                                speaker={leftSpeaker}
                                side="left"
                                onActivate={leftCarouselAction}
                              />
                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={mobileCurrent?.id ?? mobileIdx}
                                  variants={SPEAKER_CAROUSEL_FADE}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
                                  className="flex shrink-0 justify-center"
                                >
                                  <SpeakerHeroCard speaker={mobileCurrent} onOpenDetails={openSpeakerDetails} />
                                </motion.div>
                              </AnimatePresence>
                              <SpeakerPeekCard
                                speaker={rightSpeaker}
                                side="right"
                                onActivate={rightCarouselAction}
                              />
                            </>
                          ) : (
                            <AnimatePresence mode="wait">
                              <motion.div
                                key={mobileCurrent?.id ?? mobileIdx}
                                variants={SPEAKER_CAROUSEL_FADE}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
                                className="flex w-full max-w-[480px] justify-center"
                              >
                                <SpeakerHeroCard speaker={mobileCurrent} onOpenDetails={openSpeakerDetails} />
                              </motion.div>
                            </AnimatePresence>
                          )}
                        </div>
                    </div>

                    {nMobile > 1 && (
                      <div className="flex w-full max-w-lg items-center justify-center gap-2 px-4 sm:max-w-none sm:gap-2.5">
                        {filteredSpeakers.map((s, i) => (
                          <button
                            key={s.id ?? i}
                            type="button"
                            onClick={() => mobileGoToIndex(i)}
                            className={`rounded-full transition-all duration-300 ${
                              i === mobileIdx
                                ? 'h-2.5 w-2.5 scale-110 bg-[#0AB0CE] shadow-sm ring-2 ring-[#0AB0CE]/25'
                                : 'h-2 w-2 bg-[#D1D5DB] hover:bg-[#B8BCC4]'
                            }`}
                            aria-label={t('website.speakers.carouselDot', { n: i + 1 })}
                            aria-current={i === mobileIdx}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Desktop: wrapped grid */}
                  <div className="hidden lg:flex flex-wrap justify-center w-full gap-y-[22px] gap-x-[38px]">
                  {filteredSpeakers.map((speaker, index) => (
                    <motion.div
                      key={speaker.id || index}
                      variants={itemVariants}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openSpeakerDetails(speaker);
                        }
                      }}
                      className="w-[204px] max-w-full flex flex-col items-center gap-7 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0AB0CE] rounded-lg"
                      onClick={() => openSpeakerDetails(speaker)}
                    >
                      <div
                        className="relative w-full max-w-[192px] aspect-[192/184] rounded-full overflow-hidden shrink-0"
                        style={{ boxShadow: '2px 4px 9.8px rgba(144, 144, 144, 0.25)' }}
                      >
                        <SpeakerPhoto
                          src={speaker.img}
                          imgClassName="absolute inset-0 w-full h-full object-cover"
                        />
                      </div>

                      <div className="w-full flex flex-col items-center gap-2 text-center px-1">
                        <h3
                          className="w-full text-[18px] font-semibold leading-6"
                          style={{ color: '#0AB0CE' }}
                        >
                          {speaker.name}
                        </h3>
                        {speaker.position ? (
                          <p
                            className="w-full text-[14px] font-semibold leading-5"
                            style={{ color: '#B4B9C9' }}
                          >
                            {speaker.position}
                          </p>
                        ) : null}
                        {speaker.country ? (
                          <p
                            className="w-full text-[12px] font-light leading-5"
                            style={{ color: '#B4B9C9' }}
                          >
                            {speaker.country}
                          </p>
                        ) : null}
                      </div>

                      {speaker.linkedin_url ? (
                        <a
                          href={normalizeLinkedInUrl(speaker.linkedin_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center p-1 rounded hover:opacity-80 transition-opacity"
                          aria-label="LinkedIn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <LinkedInIcon />
                        </a>
                      ) : (
                        <div className="h-[14px]" aria-hidden />
                      )}
                    </motion.div>
                  ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {selectedSpeaker && (
            <>
              <motion.div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
                variants={backdropVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={closeSpeakerDetails}
              />
              <motion.div
                className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                variants={backdropVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={closeSpeakerDetails}
              >
                <motion.div
                  variants={modalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative border border-gray-100"
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#4C078C] via-[#9DD4CA] to-[#4C078C] rounded-t-3xl" />
                  <button
                    type="button"
                    className={`absolute top-5 ${isRTL ? 'left-5' : 'right-5'} w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 z-50 transition-colors`}
                    onClick={closeSpeakerDetails}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="p-6 md:p-8 pt-8">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl overflow-hidden ring-4 ring-[#9DD4CA]/30 shadow-lg mx-auto md:mx-0 flex-shrink-0 relative">
                        {selectedSpeaker.img ? (
                          <SpeakerPhoto
                            src={selectedSpeaker.img}
                            imgClassName="absolute inset-0 w-full h-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#9DD4CA]/20 to-[#4C078C]/10">
                            <AvatarFallback gender={selectedSpeaker.gender} size="lg" />
                          </div>
                        )}
                      </div>
                      <div className={`flex-1 text-center ${isRTL ? 'md:text-right' : 'md:text-left'}`}>
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                          {selectedSpeaker.name}
                        </h3>
                        <p className="text-lg font-semibold text-[#4C078C] mb-4">
                          {selectedSpeaker.role}
                        </p>
                        {selectedSpeaker.description ? (
                          <div className="mt-4">
                            <h4 className="text-base font-semibold text-gray-800 mb-2">
                              {t('website.speakers.about')}
                            </h4>
                            <div className="text-gray-600 text-sm md:text-base leading-relaxed space-y-2">
                              {selectedSpeaker.description.split('\n').map((paragraph, i) => (
                                <p key={i}>{paragraph}</p>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {selectedSpeaker.linkedin_url ? (
                          <a
                            href={normalizeLinkedInUrl(selectedSpeaker.linkedin_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-4 text-[#422A7E] font-semibold text-sm hover:underline"
                          >
                            <LinkedInIcon />
                            LinkedIn
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
};

export default Speakers;
